import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { AlertCircle, Globe, Search } from "lucide-react";
import type { FpMessagePart } from "../../../../../../schemas/codegen";
import { ToolError } from "../shared";
import { SearchResultItem } from "./SearchResultItem";
import { useWebSearchData } from "./hooks";

interface WebSearchProps {
  part: Extract<FpMessagePart, { type: "tool-call" }>;
}

export function WebSearch({ part }: WebSearchProps) {
  const isError = part.status === "error";
  const isComplete = part.status === "complete";
  const isPending = part.status === "pending";

  const { searchQuery, searchResults } = useWebSearchData(part);

  const displayName = isPending
    ? "Searching the web..."
    : isComplete
      ? "Web search"
      : "Web search failed";

  const statusIcon = isPending ? (
    <Search className="w-3 h-3 animate-pulse" />
  ) : isComplete && searchResults.length > 0 ? (
    <Globe className="w-3 h-3 text-muted-foreground" />
  ) : isError ? (
    <AlertCircle className="w-3 h-3 text-warning" />
  ) : null;

  return (
    <Accordion type="single" collapsible className="w-full overflow-x-auto">
      <AccordionItem
        value="web-search"
        className="rounded-lg bg-secondary/50 border-b-0 overflow-hidden"
      >
        <AccordionTrigger className="hover:no-underline h-10 px-4 bg-input max-w-full">
          <div className="flex items-center gap-2 min-w-0 overflow-hidden">
            <div className="flex items-center gap-3 font-normal flex-shrink-0">
              <Search className="w-4 h-4" />
              <span>{displayName}</span>
            </div>

            {searchQuery && (
              <div className="text-xs text-muted-foreground min-w-0 flex-1 overflow-hidden">
                <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded truncate block">
                  {searchQuery}
                </code>
              </div>
            )}

            {statusIcon && (
              <div className="flex-shrink-0 mx-1">{statusIcon}</div>
            )}
          </div>
        </AccordionTrigger>

        <AccordionContent className="px-4 py-2 border-t">
          {isError && part.result ? <ToolError result={part.result} /> : null}

          {isComplete && searchResults.length > 0 && (
            <div className="space-y-2">
              {searchResults.map((result, index) => (
                <SearchResultItem
                  key={`${result.url}-${index}`}
                  result={result}
                />
              ))}
            </div>
          )}

          {isComplete && searchResults.length === 0 && !isError && (
            <div className="text-sm text-muted-foreground text-center py-4">
              No search results found
            </div>
          )}

          {isPending && (
            <div className="flex items-center justify-center py-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Search className="w-4 h-4 animate-pulse" />
                <span>Searching...</span>
              </div>
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
